import React, { lazy } from "react";

const AdminLogin = lazy(() =>
  import(/* webpackChunkName: "admin-login" */ "../pages/admin/Login")
);
// Iter273.3 — honeypot 404 for the legacy /admin/login paths. Re-uses the
// site-wide NotFound page so probes against the rotated URL see exactly the
// same response as any other unknown route. No hint, no redirect.
// Iter273.4 — wrapper component fires a single best-effort beacon to
// `/api/public/_telemetry/route-404` on mount so the honeypot becomes an
// active intel surface (Slack alert + hit log) without changing what the
// scanner actually sees.
const AdminLoginHoneypot = lazy(() =>
  import(/* webpackChunkName: "admin-login-honeypot" */ "../components/admin/AdminLoginHoneypot")
);
const AdminForgotPasswordPage = lazy(() =>
  import(/* webpackChunkName: "admin-password-reset" */ "../pages/admin/PasswordReset")
    .then(m => ({ default: m.AdminForgotPassword }))
);
const AdminResetPasswordPage = lazy(() =>
  import(/* webpackChunkName: "admin-password-reset" */ "../pages/admin/PasswordReset")
    .then(m => ({ default: m.AdminResetPassword }))
);
const AcceptInvite = lazy(() =>
  import(/* webpackChunkName: "admin-accept-invite" */ "../pages/admin/AcceptInvite")
);
const Setup2FA = lazy(() =>
  import(/* webpackChunkName: "admin-setup-2fa" */ "../pages/admin/Setup2FA")
);
// Iter276.3 — WebAuthn / passkey management surface.
const PasskeySettings = lazy(() =>
  import(/* webpackChunkName: "admin-passkeys" */ "../pages/admin/PasskeySettings")
);
const AdminOverview = lazy(() =>
  import(/* webpackChunkName: "admin-dashboard" */ "../pages/admin/Dashboard")
);
const UsersPage = lazy(() =>
  import(/* webpackChunkName: "admin-users" */ "../pages/admin/Users")
);
const OrgsPage = lazy(() =>
  import(/* webpackChunkName: "admin-orgs" */ "../pages/admin/Orgs")
);
const OrgDetail = lazy(() =>
  import(/* webpackChunkName: "admin-org-detail" */ "../pages/admin/OrgDetail")
);
const ForecastsPage = lazy(() =>
  import(/* webpackChunkName: "admin-forecasts" */ "../pages/admin/Forecasts")
);
const Consultations = lazy(() =>
  import(/* webpackChunkName: "admin-consultations" */ "../pages/admin/Consultations")
);
const Leads = lazy(() =>
  import(/* webpackChunkName: "admin-leads" */ "../pages/admin/Leads")
);
const Customers = lazy(() =>
  import(/* webpackChunkName: "admin-customers" */ "../pages/admin/Customers")
);
const Newsletter = lazy(() =>
  import(/* webpackChunkName: "admin-newsletter" */ "../pages/admin/Newsletter")
);
const DripAudit = lazy(() =>
  import(/* webpackChunkName: "admin-drip-audit" */ "../pages/admin/DripAudit")
);
const Settings = lazy(() =>
  import(/* webpackChunkName: "admin-settings" */ "../pages/admin/Settings")
);
const AdminReports = lazy(() =>
  import(/* webpackChunkName: "admin-reports" */ "../pages/admin/Placeholders").then(
    (m) => ({ default: m.Reports })
  )
);
const Revenue = lazy(() =>
  import(/* webpackChunkName: "admin-revenue" */ "../pages/admin/Revenue")
);
const Renewals = lazy(() =>
  import(/* webpackChunkName: "admin-renewals" */ "../pages/admin/Renewals")
);
const SiteFlags = lazy(() =>
  import(/* webpackChunkName: "admin-site-flags" */ "../pages/admin/SiteFlags")
);
const DemoCleanup = lazy(() =>
  import(/* webpackChunkName: "admin-demo-cleanup" */ "../pages/admin/DemoCleanup")
);
const DemoSessions = lazy(() =>
  import(/* webpackChunkName: "admin-demo-sessions" */ "../pages/admin/DemoSessions")
);
const TenantSeatBilling = lazy(() =>
  import(/* webpackChunkName: "admin-tenant-seat-billing" */ "../pages/admin/TenantSeatBilling")
);
const SetupStatus = lazy(() =>
  import(/* webpackChunkName: "admin-setup-status" */ "../pages/admin/SetupStatus")
);
const OrgAnalytics = lazy(() =>
  import(/* webpackChunkName: "admin-org-analytics" */ "../pages/admin/OrgAnalytics")
);
const AdminAuditsQueue = lazy(() =>
  import(/* webpackChunkName: "admin-audits-queue" */ "../pages/admin/AuditsQueue")
);
const AdminAuditSalesLeads = lazy(() =>
  import(/* webpackChunkName: "admin-audit-sales-leads" */ "../pages/admin/AuditSalesLeads")
);
const AdminSalesReps = lazy(() =>
  import(/* webpackChunkName: "admin-sales-reps" */ "../pages/admin/SalesReps")
);
const AdminSalesRepPipeline = lazy(() =>
  import(/* webpackChunkName: "admin-sales-rep-pipeline" */ "../pages/admin/SalesRepPipeline")
);
const AdminSalesPlaybook = lazy(() =>
  import(/* webpackChunkName: "admin-sales-playbook" */ "../pages/admin/SalesPlaybook")
);
const AdminSalesTalkTrackSandbox = lazy(() =>
  import(/* webpackChunkName: "admin-sales-talk-track-sandbox" */ "../pages/admin/SalesTalkTrackSandbox")
);
const AdminSalesTalkTrackTemplates = lazy(() =>
  import(/* webpackChunkName: "admin-sales-talk-track-templates" */ "../pages/admin/SalesTalkTrackTemplates")
);
const AdminAccountExecutives = lazy(() =>
  import(/* webpackChunkName: "admin-account-executives" */ "../pages/admin/AccountExecutives")
);
const AdminNudgeStats = lazy(() =>
  import(/* webpackChunkName: "admin-nudge-stats" */ "../pages/admin/NudgeStats")
);
const AdminAuditDetail = lazy(() =>
  import(/* webpackChunkName: "admin-audit-detail" */ "../pages/admin/AuditDetail")
);
const AdminChangelog = lazy(() =>
  import(/* webpackChunkName: "admin-changelog" */ "../pages/admin/Changelog")
);
const AdminMethodologyReviewers = lazy(() =>
  import(
    /* webpackChunkName: "admin-methodology-reviewers" */ "../pages/admin/MethodologyReviewers"
  )
);
const WikiAdminPage = lazy(() =>
  import(/* webpackChunkName: "admin-wiki" */ "../pages/admin/Wiki")
);
const WikiEditorPage = lazy(() =>
  import(/* webpackChunkName: "admin-wiki-editor" */ "../pages/admin/WikiEditor")
);
const WikiNavEditor = lazy(() =>
  import(/* webpackChunkName: "admin-wiki-nav" */ "../pages/admin/WikiNavEditor")
);
const WikiCoveragePage = lazy(() =>
  import(/* webpackChunkName: "admin-wiki-coverage" */ "../pages/admin/WikiCoverage")
);
const WikiSearchAnalyticsPage = lazy(() =>
  import(/* webpackChunkName: "admin-wiki-search-analytics" */ "../pages/admin/WikiSearchAnalytics")
);
const QuickstartDripPage = lazy(() =>
  import(/* webpackChunkName: "admin-quickstart-drip" */ "../pages/admin/QuickstartDrip")
);
const QelliInsightsPage = lazy(() =>
  import(/* webpackChunkName: "admin-qelli-insights" */ "../pages/admin/QelliInsights")
);
const QelliWeeklyReportPage = lazy(() =>
  import(/* webpackChunkName: "admin-qelli-weekly" */ "../pages/admin/QelliWeeklyReport")
);
const QelliConversionPage = lazy(() =>
  import(/* webpackChunkName: "admin-qelli-conversion" */ "../pages/admin/QelliConversion")
);
const SocialConnectionsPage = lazy(() =>
  import(/* webpackChunkName: "admin-social-connections" */ "../pages/admin/SocialConnections")
);
const CompliancePortalPage = lazy(() =>
  import(/* webpackChunkName: "admin-compliance-portal" */ "../pages/admin/CompliancePortal")
);
const CompliancePoliciesPage = lazy(() =>
  import(/* webpackChunkName: "admin-compliance-policies" */ "../pages/admin/CompliancePolicies")
);
const CompliancePolicyEditorPage = lazy(() =>
  import(/* webpackChunkName: "admin-compliance-policy-editor" */ "../pages/admin/CompliancePolicyEditor")
);
const ComplianceGapClosurePage = lazy(() =>
  import(/* webpackChunkName: "admin-compliance-gap-closure" */ "../pages/admin/ComplianceGapClosure")
);
const ComplianceConnectorsPage = lazy(() =>
  import(/* webpackChunkName: "admin-compliance-connectors" */ "../pages/admin/ComplianceConnectors")
);
const ComplianceEvidencePackPage = lazy(() =>
  import(/* webpackChunkName: "admin-compliance-evidence-pack" */ "../pages/admin/ComplianceEvidencePack")
);
const CisoLairPage = lazy(() =>
  import(/* webpackChunkName: "admin-ciso-lair" */ "../pages/admin/CisoLair")
);
const SafetyConsolePage = lazy(() =>
  import(/* webpackChunkName: "admin-safety-console" */ "../pages/admin/SafetyConsole")
);
const PolicyVersionsPage = lazy(() =>
  import(/* webpackChunkName: "admin-policy-versions" */ "../pages/admin/PolicyVersions")
);
const AdminDecksPage = lazy(() =>
  import(/* webpackChunkName: "admin-decks" */ "../pages/admin/AdminDecks")
);
const AdminDocFeedbackPage = lazy(() =>
  import(/* webpackChunkName: "admin-doc-feedback" */ "../pages/admin/AdminDocFeedback")
);
const LlmStatusPage = lazy(() =>
  import(/* webpackChunkName: "admin-llm-status" */ "../pages/admin/LlmStatus")
);
const SocialQueuePage = lazy(() =>
  import(/* webpackChunkName: "admin-social-queue" */ "../pages/admin/SocialQueue")
);
const AuditIntentsPage = lazy(() =>
  import(/* webpackChunkName: "admin-audit-intents" */ "../pages/admin/AuditIntents")
);
// Phase 5.5 — QorTrace Labs admin (gated to lab_pm / lab_engineer / owner).
const LabsAdminPage = lazy(() =>
  import(/* webpackChunkName: "admin-labs" */ "../pages/admin/Labs")
);
const LabsRoadmapAdminPage = lazy(() =>
  import(/* webpackChunkName: "admin-labs-roadmap" */ "../pages/admin/LabsRoadmap")
);
const LabsEngagementDetailPage = lazy(() =>
  import(/* webpackChunkName: "admin-labs-engagement" */ "../pages/admin/LabsEngagementDetail")
);
const LabsInsightsAdminPage = lazy(() =>
  import(/* webpackChunkName: "admin-labs-insights" */ "../pages/admin/LabsInsightsAdmin")
);
const LabsInsightsCadencePage = lazy(() =>
  import(/* webpackChunkName: "admin-labs-cadence" */ "../pages/admin/LabsInsightsCadence")
);
const LabsChannelROIPage = lazy(() =>
  import(/* webpackChunkName: "admin-labs-roi" */ "../pages/admin/LabsChannelROI")
);
const LabsFunnelPage = lazy(() =>
  import(/* webpackChunkName: "admin-labs-funnel" */ "../pages/admin/LabsFunnel")
);
const AssetHealthPage = lazy(() =>
  import(/* webpackChunkName: "admin-asset-health" */ "../pages/admin/AssetHealth")
);
const AdminNdaCenterPage = lazy(() =>
  import(/* webpackChunkName: "admin-nda" */ "../pages/admin/AdminNdaCenter")
);
// Phase 10 — Legal Center (iter 1: regulatory feed, iter 2: dashboard).
const AdminLegalRegulatoryFeedPage = lazy(() =>
  import(/* webpackChunkName: "admin-legal" */ "../pages/admin/legal/RegulatoryFeed")
);
const AdminLegalDashboardPage = lazy(() =>
  import(/* webpackChunkName: "admin-legal" */ "../pages/admin/legal/Dashboard")
);
const AdminLegalInboxHealthPage = lazy(() =>
  import(/* webpackChunkName: "admin-legal" */ "../pages/admin/legal/InboxHealth")
);
const AdminLegalContractsListPage = lazy(() =>
  import(/* webpackChunkName: "admin-legal" */ "../pages/admin/legal/ContractsList")
);
const AdminLegalContractDetailPage = lazy(() =>
  import(/* webpackChunkName: "admin-legal" */ "../pages/admin/legal/ContractDetail")
);
// Phase 10 iter 4 — Matters + external counsel invites
const AdminLegalMattersListPage = lazy(() =>
  import(/* webpackChunkName: "admin-legal" */ "../pages/admin/legal/MattersList")
);
const AdminLegalMatterDetailPage = lazy(() =>
  import(/* webpackChunkName: "admin-legal" */ "../pages/admin/legal/MatterDetail")
);

// Phase 7 finishing pass — Enterprise application intake admin views
const AdminEnterpriseIntakesPage = lazy(() =>
  import(/* webpackChunkName: "admin-enterprise" */ "../pages/admin/EnterpriseIntakes")
);
const AdminEnterpriseIntakeDetailPage = lazy(async () => {
  const m = await import(/* webpackChunkName: "admin-enterprise" */ "../pages/admin/EnterpriseIntakes");
  return { default: m.AdminEnterpriseIntakeDetail };
});
const AdminEngagementsInboxPage = lazy(() =>
  import(/* webpackChunkName: "admin-engagements-inbox" */ "../pages/admin/EngagementsInbox")
);

// M3a — QorBOM partner-application admin review
const AdminQorbomPartnersPage = lazy(() =>
  import(/* webpackChunkName: "admin-qorbom" */ "../pages/admin/qorbom/PartnerApplications")
);
// M3a Step 2 — Partner-tenant management (issue keys, branding, pricing)
const AdminQorbomTenantsPage = lazy(() =>
  import(/* webpackChunkName: "admin-qorbom" */ "../pages/admin/qorbom/Tenants")
);
// Iter130 — QorBOM launch dashboard (per-brand acquisition + conversion + revenue)
const AdminQorbomDashboardPage = lazy(() =>
  import(/* webpackChunkName: "admin-qorbom" */ "../pages/admin/QorbomDashboard")
);
// Iter131 Wave 1 — Operator Command Center
const AdminQorbomTenantDetailPage = lazy(() =>
  import(/* webpackChunkName: "admin-qorbom-w1" */ "../pages/admin/qorbom/TenantDetail")
);
const AdminQorbomOnboardingWizardPage = lazy(() =>
  import(/* webpackChunkName: "admin-qorbom-w1" */ "../pages/admin/qorbom/OnboardingWizard")
);
const AdminQorbomBrandStudioPage = lazy(() =>
  import(/* webpackChunkName: "admin-qorbom-w1" */ "../pages/admin/qorbom/BrandStudio")
);
const AdminQorbomAuditTrailPage = lazy(() =>
  import(/* webpackChunkName: "admin-qorbom-w1" */ "../pages/admin/qorbom/AuditTrail")
);
// Iter131 Wave 2 stubs (Coming Soon)
const AdminQorbomEmbedDomainsPage = lazy(() =>
  import(/* webpackChunkName: "admin-qorbom-w2" */ "../pages/admin/qorbom/EmbedDomains")
);
const AdminQorbomRevenuePage = lazy(() =>
  import(/* webpackChunkName: "admin-qorbom-w2" */ "../pages/admin/qorbom/Revenue")
);
const AdminQorbomEmailHealthPage = lazy(() =>
  import(/* webpackChunkName: "admin-qorbom-w2" */ "../pages/admin/qorbom/EmailHealth")
);
const AdminQorbomSupportPage = lazy(() =>
  import(/* webpackChunkName: "admin-qorbom-w2" */ "../pages/admin/qorbom/Support")
);
// Wave 3 — Referral / Affiliate program
const AdminReferralLeaderboardPage = lazy(() =>
  import(/* webpackChunkName: "admin-referrals" */ "../pages/admin/ReferralLeaderboard")
);
// Iter254 — Incident composer
const AdminIncidentsPage = lazy(() =>
  import(/* webpackChunkName: "admin-incidents" */ "../pages/admin/IncidentsAdmin")
);
// Iter256 — HNDL curated wallets admin
const AdminHndlCuratedPage = lazy(() =>
  import(/* webpackChunkName: "admin-hndl-curated" */ "../pages/admin/hndl/CuratedAdmin")
);

// Iter273.3 — Admin login path rotated from `/admin/login` to `/qor-ops/access`
// to harden against credential-stuffing scanners that brute-force the obvious
// `/admin/login` URL. The old paths are kept as honeypot 404s (reuse
// `NotFound`) so bot probes get a generic "page not found" with zero hint
// that the route ever existed — no redirect, no path leak.
// IMPORTANT: keep `robots.txt` `Disallow: /qor-ops/` in lockstep so the new
// path also stays out of public search indexes.
const adminRoutes = [
  { path: "/qor-ops/access", element: <AdminLogin /> },
  { path: "/qor-ops/access/:orgSlug", element: <AdminLogin /> },
  // Honeypot 404s for the legacy `/admin/login` paths. No redirect = no leak
  // about where the new path lives.
  { path: "/admin/login", element: <AdminLoginHoneypot /> },
  { path: "/admin/login/:orgSlug", element: <AdminLoginHoneypot /> },
  { path: "/admin/forgot",          element: <AdminForgotPasswordPage /> },
  { path: "/admin/forgot-password", element: <AdminForgotPasswordPage /> },
  // Iter276.3 — WebAuthn / passkey management.
  { path: "/admin/security/passkeys", element: <PasskeySettings /> },
  { path: "/admin/reset-password",  element: <AdminResetPasswordPage  /> },
  { path: "/admin/accept-invite", element: <AcceptInvite /> },
  { path: "/admin/setup-2fa", element: <Setup2FA /> },
  { path: "/admin", element: <AdminOverview /> },
  { path: "/admin/users", element: <UsersPage /> },
  { path: "/admin/orgs", element: <OrgsPage /> },
  { path: "/admin/orgs/:orgId", element: <OrgDetail /> },
  { path: "/admin/forecasts", element: <ForecastsPage /> },
  { path: "/admin/customers", element: <Customers /> },
  { path: "/admin/newsletter", element: <Newsletter /> },
  { path: "/admin/drip-audit", element: <DripAudit /> },
  { path: "/admin/revenue", element: <Revenue /> },
  { path: "/admin/renewals", element: <Renewals /> },
  { path: "/admin/site-flags", element: <SiteFlags /> },
  { path: "/admin/demo-cleanup", element: <DemoCleanup /> },
  { path: "/admin/demo-sessions", element: <DemoSessions /> },
  { path: "/admin/billing/seats", element: <TenantSeatBilling /> },
  { path: "/admin/setup", element: <SetupStatus /> },
  { path: "/admin/orgs/:orgId/analytics", element: <OrgAnalytics /> },
  { path: "/admin/audits", element: <AdminAuditsQueue /> },
  { path: "/admin/audits/sales-leads", element: <AdminAuditSalesLeads /> },
  { path: "/admin/sales/reps", element: <AdminSalesReps /> },
  { path: "/admin/sales/reps/:slug", element: <AdminSalesRepPipeline /> },
  { path: "/admin/sales/talk-tracks", element: <AdminSalesTalkTrackSandbox /> },
  { path: "/admin/sales/talk-tracks/templates", element: <AdminSalesTalkTrackTemplates /> },
  { path: "/admin/account-executives", element: <AdminAccountExecutives /> },
  { path: "/admin/sales/playbook", element: <AdminSalesPlaybook /> },
  { path: "/admin/sales/playbook/:persona", element: <AdminSalesPlaybook /> },
  { path: "/admin/audits/:id", element: <AdminAuditDetail /> },
  { path: "/admin/nudge-experiments", element: <AdminNudgeStats /> },
  { path: "/admin/changelog", element: <AdminChangelog /> },
  { path: "/admin/consultations", element: <Consultations /> },
  { path: "/admin/leads", element: <Leads /> },
  { path: "/admin/leads/methodology-reviewers", element: <AdminMethodologyReviewers /> },
  { path: "/admin/wiki", element: <WikiAdminPage /> },
  { path: "/admin/wiki/coverage", element: <WikiCoveragePage /> },
  { path: "/admin/wiki/search-analytics", element: <WikiSearchAnalyticsPage /> },
  { path: "/admin/quickstart-drip", element: <QuickstartDripPage /> },
  { path: "/admin/qelli/insights", element: <QelliInsightsPage /> },
  { path: "/admin/qelli/weekly-report", element: <QelliWeeklyReportPage /> },
  { path: "/admin/qelli/conversion", element: <QelliConversionPage /> },
  { path: "/admin/social", element: <SocialConnectionsPage /> },
  { path: "/admin/compliance", element: <CompliancePortalPage /> },
  { path: "/admin/compliance/policies", element: <CompliancePoliciesPage /> },
  { path: "/admin/compliance/policies/:policyId", element: <CompliancePolicyEditorPage /> },
  { path: "/admin/compliance/gap-closure", element: <ComplianceGapClosurePage /> },
  { path: "/admin/compliance/connectors", element: <ComplianceConnectorsPage /> },
  { path: "/admin/compliance/evidence-pack", element: <ComplianceEvidencePackPage /> },
  { path: "/admin/ciso-lair", element: <CisoLairPage /> },
  { path: "/admin/safety", element: <SafetyConsolePage /> },
  { path: "/admin/compliance/policy-versions", element: <PolicyVersionsPage /> },
  { path: "/admin/decks", element: <AdminDecksPage /> },
  { path: "/admin/doc-feedback", element: <AdminDocFeedbackPage /> },
  { path: "/admin/llm-status", element: <LlmStatusPage /> },
  { path: "/admin/social-queue", element: <SocialQueuePage /> },
  { path: "/admin/ciso-lair/audit-intents", element: <AuditIntentsPage /> },
  // Labs admin
  { path: "/admin/labs", element: <LabsAdminPage /> },
  { path: "/admin/labs/roadmap", element: <LabsRoadmapAdminPage /> },
  { path: "/admin/labs/engagements/:eng_id", element: <LabsEngagementDetailPage /> },
  { path: "/admin/labs/insights", element: <LabsInsightsAdminPage /> },
  { path: "/admin/labs/insights/cadence", element: <LabsInsightsCadencePage /> },
  { path: "/admin/labs/channel-roi", element: <LabsChannelROIPage /> },
  { path: "/admin/labs/funnel",      element: <LabsFunnelPage /> },
  { path: "/admin/ops/asset-health", element: <AssetHealthPage /> },
  { path: "/admin/wiki/new", element: <WikiEditorPage /> },
  { path: "/admin/wiki/edit/:slug", element: <WikiEditorPage /> },
  { path: "/admin/wiki/edit/:slug/*", element: <WikiEditorPage /> },
  { path: "/admin/wiki/nav", element: <WikiNavEditor /> },
  { path: "/admin/reports", element: <AdminReports /> },
  { path: "/admin/nda",     element: <AdminNdaCenterPage /> },
  { path: "/admin/legal",                 element: <AdminLegalDashboardPage /> },
  { path: "/admin/legal/dashboard",       element: <AdminLegalDashboardPage /> },
  { path: "/admin/legal/regulatory-feed", element: <AdminLegalRegulatoryFeedPage /> },
  { path: "/admin/legal/inbox-health",    element: <AdminLegalInboxHealthPage /> },
  { path: "/admin/legal/contracts",       element: <AdminLegalContractsListPage /> },
  { path: "/admin/legal/contracts/:contractId", element: <AdminLegalContractDetailPage /> },
  { path: "/admin/legal/matters",         element: <AdminLegalMattersListPage /> },
  { path: "/admin/legal/matters/:matterId", element: <AdminLegalMatterDetailPage /> },
  { path: "/admin/enterprise-intake",      element: <AdminEnterpriseIntakesPage /> },
  { path: "/admin/enterprise-intake/:intakeId", element: <AdminEnterpriseIntakeDetailPage /> },
  { path: "/admin/enterprise-engagements/inbox", element: <AdminEngagementsInboxPage /> },
  // M3a — QorBOM partner-applications admin review
  { path: "/admin/qorbom/dashboard",       element: <AdminQorbomDashboardPage /> },
  { path: "/admin/qorbom/partners",        element: <AdminQorbomPartnersPage /> },
  { path: "/admin/qorbom/tenants",         element: <AdminQorbomTenantsPage /> },
  // Iter131 Wave 1 — Operator Command Center
  { path: "/admin/qorbom/tenants/:tenantId", element: <AdminQorbomTenantDetailPage /> },
  { path: "/admin/qorbom/wizard/new",        element: <AdminQorbomOnboardingWizardPage /> },
  { path: "/admin/qorbom/wizard/:draftId",   element: <AdminQorbomOnboardingWizardPage /> },
  { path: "/admin/qorbom/branding",          element: <AdminQorbomBrandStudioPage /> },
  { path: "/admin/qorbom/audit",             element: <AdminQorbomAuditTrailPage /> },
  // Iter131 Wave 2 stubs
  { path: "/admin/qorbom/embeds",            element: <AdminQorbomEmbedDomainsPage /> },
  { path: "/admin/qorbom/revenue",           element: <AdminQorbomRevenuePage /> },
  { path: "/admin/qorbom/email-health",      element: <AdminQorbomEmailHealthPage /> },
  { path: "/admin/qorbom/support",           element: <AdminQorbomSupportPage /> },
  // Wave 3 — Referral / Affiliate program
  { path: "/admin/referrals",                element: <AdminReferralLeaderboardPage /> },
  // Iter254 — Incident composer
  { path: "/admin/incidents",                element: <AdminIncidentsPage /> },
  // Iter256 — HNDL curated wallets admin
  { path: "/admin/hndl/curated",             element: <AdminHndlCuratedPage /> },
  { path: "/admin/settings", element: <Settings /> },
];

export default adminRoutes;
